/**
 * DataShield Governance Portal - Main Application
 * 
 * Entry point for the React application with routing.
 * 
 * @module App
 */

import React, { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useStore, useIsAuthenticated, useTheme } from '@/store';
import { api } from '@/services/api';

// Styles
import '@/styles/tokens.css';
import '@/styles/base.css';

// Lazy load pages for code splitting
const Dashboard = React.lazy(() => import('@/pages/Dashboard'));
const AIHub = React.lazy(() => import('@/pages/AIHub'));
const Login = React.lazy(() => import('@/pages/Login'));
const AdminControlPlane = React.lazy(() => import('@/pages/AdminControlPlane'));
const DeveloperLineage = React.lazy(() => import('@/pages/DeveloperLineage'));
const PythonProcessor = React.lazy(() => import('@/pages/PythonProcessor'));
const CostOptimization = React.lazy(() => import('@/pages/CostOptimization'));

// Data Governance pages (Thread2 React conversion)
const DataCatalog = React.lazy(() => import('@/pages/catalog'));
const LineageViewer = React.lazy(() => import('@/pages/lineage'));
const DecisionEngine = React.lazy(() => import('@/pages/governance/DecisionEngine'));

// Built pages - wire into router
const ControlLibrary = React.lazy(() => import('@/pages/controls/ControlLibrary'));
const BusinessGlossary = React.lazy(() => import('@/pages/glossary/BusinessGlossary'));
const Discovery = React.lazy(() => import('@/pages/discovery/Discovery'));
const PrivacyOps = React.lazy(() => import('@/pages/privacy/PrivacyHub'));
const RetentionManagement = React.lazy(() => import('@/pages/retention/RetentionManagement'));
const OnboardingPage = React.lazy(() => import('@/pages/setup-onboarding/OnboardingPage'));
const ConnectionsPage = React.lazy(() => import('@/pages/setup-onboarding/ConnectionsPage'));

// New v0.41.1 pages
const AIOperations = React.lazy(() => import('@/pages/ai-ops/AIOperations'));
const TPRMHub = React.lazy(() => import('@/pages/tprm/TPRMHub'));
const ComplianceDashboard = React.lazy(() => import('@/pages/compliance/ComplianceDashboard'));
const SettingsPage = React.lazy(() => import('@/pages/settings/SettingsPage'));
const DataMovement = React.lazy(() => import('@/pages/data-movement/DataMovement'));

// Hotfix4 pages (formerly placeholders)
const SmartAlerts = React.lazy(() => import('@/pages/alerts/SmartAlerts'));
const PolicyManager = React.lazy(() => import('@/pages/policy-manager/PolicyManager'));
const AuditLog = React.lazy(() => import('@/pages/audit-log/AuditLog'));
const TagTaxonomy = React.lazy(() => import('@/pages/tag-taxonomy/TagTaxonomy'));
const WorkflowsPage = React.lazy(() => import('@/pages/workflows/WorkflowsPage'));
const PlaybooksPage = React.lazy(() => import('@/pages/playbooks/PlaybooksPage'));
const BlockchainVerify = React.lazy(() => import('@/pages/blockchain-verify/BlockchainVerify'));

// Detail pages
const AssetDetail = React.lazy(() => import('@/pages/catalog/AssetDetail'));
const VendorDetail = React.lazy(() => import('@/pages/tprm/VendorDetail'));

// Loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
    </div>
  );
}

// Protected route wrapper - auto-authenticates in dev mode
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const setUser = useStore((state) => state.setUser);
  
  // Dev mode: auto-authenticate if no auth configured
  useEffect(() => {
    if (!isAuthenticated) {
      const stored = sessionStorage.getItem('datashield_user');
      if (stored) {
        try { setUser(JSON.parse(stored)); } catch {}
      } else {
        const devUser = {
          id: 'dev-user',
          displayName: 'Data_steward',
          email: 'admin@datashield.io',
          role: 'admin' as const,
          permissions: ['read:all', 'write:all', 'delete:all'] as any[],
          lastActiveAt: new Date().toISOString(),
          createdAt: '2024-01-01T00:00:00Z',
          preferences: {
            theme: 'light' as const,
            language: 'en',
            timezone: 'America/Chicago',
            dateFormat: 'US' as const,
            timeFormat: '12h' as const,
            homeScreen: 'manual' as const,
            notifications: { email: true, inApp: true, slack: false, digest: 'daily' as const },
            ai: { enabled: true, autoSuggest: true, confidenceThreshold: 0.85, supervision: 'notify' as const },
            accessibility: { reduceMotion: false, highContrast: false, fontSize: 'medium' as const, screenReader: false },
          },
        };
        setUser(devUser as any);
        sessionStorage.setItem('datashield_user', JSON.stringify(devUser));
      }
    }
  }, [isAuthenticated, setUser]);
  
  // Wait for user to be set before rendering children
  if (!isAuthenticated) return <PageLoader />;
  return <>{children}</>;
}

// Error boundary for lazy-loaded pages
class PageErrorBoundary extends React.Component<
  { children: React.ReactNode; fallbackTitle?: string },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '60px 40px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ margin: '0 0 8px' }}>{this.props.fallbackTitle || 'Page Error'}</h2>
          <p style={{ color: '#64748b', maxWidth: '500px', margin: '0 auto 16px' }}>
            This page encountered an error while rendering. Try refreshing or navigating to another page.
          </p>
          <details style={{ textAlign: 'left', maxWidth: '600px', margin: '0 auto', fontSize: '12px', color: '#94a3b8' }}>
            <summary style={{ cursor: 'pointer', marginBottom: '8px' }}>Error details</summary>
            <pre style={{ background: '#f1f5f9', padding: '12px', borderRadius: '8px', overflow: 'auto' }}>
              {this.state.error?.message}{'\n'}{this.state.error?.stack}
            </pre>
          </details>
        </div>
      );
    }
    return this.props.children;
  }
}

// Theme provider effect
function ThemeProvider({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  
  useEffect(() => {
    const resolvedTheme = theme === 'system'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : theme;
    document.documentElement.setAttribute('data-theme', resolvedTheme);
  }, [theme]);
  
  // Listen for system theme changes
  useEffect(() => {
    if (theme !== 'system') return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      document.documentElement.setAttribute('data-theme', e.matches ? 'dark' : 'light');
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);
  
  return <>{children}</>;
}

// Main App component
export function App() {
  const setUser = useStore((state) => state.setUser);
  const isAuthenticated = useIsAuthenticated();
  
  // Check auth on mount
  useEffect(() => {
    if (api.auth.isAuthenticated() && !isAuthenticated) {
      // Fetch user profile
      api.get('/users/me')
        .then(setUser)
        .catch(() => {
          api.auth.logout();
        });
    }
  }, [setUser, isAuthenticated]);
  
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            
            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <Dashboard />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            <Route
              path="/ai-hub"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <AIHub />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            
            {/* Data Catalog - Asset Detail */}
            <Route
              path="/catalog/assets/:id"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <AssetDetail />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            
            {/* Data Catalog (Thread 2 Governance UI - React conversion) */}
            <Route
              path="/catalog/*"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <DataCatalog />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            
            {/* Data Lineage (Thread 2 Governance UI - D3.js wrapped in React) */}
            <Route
              path="/lineage/*"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <LineageViewer />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            
            {/* Developer Lineage - Pipeline Studio (from Conduit lineage-viz module) */}
            <Route
              path="/pipelines/*"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <DeveloperLineage />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            
            {/* Python Processor - Template-based processing (from Conduit python-processor module) */}
            <Route
              path="/python-processor/*"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <PythonProcessor />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            
            {/* Cost Optimization - Snowflake cost analysis (from Conduit cost-optimization module) */}
            <Route
              path="/cost-optimization/*"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <CostOptimization />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            
            {/* Business Glossary */}
            <Route
              path="/glossary"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <BusinessGlossary />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            
            {/* Universal Discovery */}
            <Route
              path="/discovery"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <Discovery />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            {/* Control Library */}
            <Route
              path="/controls"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <ControlLibrary />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            <Route
              path="/compliance"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <ComplianceDashboard />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            <Route
              path="/privacy/*"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <PrivacyOps />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            <Route
              path="/tprm/vendor/:id"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <VendorDetail />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            <Route
              path="/tprm/*"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <TPRMHub />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            {/* Master Data Management (Thread 2 Governance UI) */}
            <Route
              path="/mdm/*"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <DecisionEngine />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            <Route
              path="/retention/*"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <RetentionManagement />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            
            {/* Admin Control Plane - AI Agent Management (from Conduit admin-ui module) */}
            <Route
              path="/control-plane/*"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <AdminControlPlane />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            
            {/* AI Operations Hub */}
            {/* AI Operations */}
            <Route
              path="/ai/*"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <AIOperations />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <SettingsPage />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            <Route
              path="/import"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <DataMovement />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            <Route
              path="/export"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <DataMovement />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            
            {/* Setup & Onboarding */}
            <Route
              path="/setup"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <OnboardingPage />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            <Route
              path="/connections"
              element={
                <ProtectedRoute><PageErrorBoundary>
                  <ConnectionsPage />
                </PageErrorBoundary></ProtectedRoute>
              }
            />
            
            {/* Missing sidebar routes — need full pages built */}
            <Route path="/alerts" element={<ProtectedRoute><PageErrorBoundary><SmartAlerts /></PageErrorBoundary></ProtectedRoute>} />
            <Route path="/tags" element={<ProtectedRoute><PageErrorBoundary><TagTaxonomy /></PageErrorBoundary></ProtectedRoute>} />
            <Route path="/policies" element={<ProtectedRoute><PageErrorBoundary><PolicyManager /></PageErrorBoundary></ProtectedRoute>} />
            <Route path="/audit" element={<ProtectedRoute><PageErrorBoundary><AuditLog /></PageErrorBoundary></ProtectedRoute>} />
            <Route path="/blockchain" element={<ProtectedRoute><PageErrorBoundary><BlockchainVerify /></PageErrorBoundary></ProtectedRoute>} />
            <Route path="/workflows" element={<ProtectedRoute><PageErrorBoundary><WorkflowsPage /></PageErrorBoundary></ProtectedRoute>} />
            <Route path="/playbooks" element={<ProtectedRoute><PageErrorBoundary><PlaybooksPage /></PageErrorBoundary></ProtectedRoute>} />

            {/* Default redirect */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ThemeProvider>
  );
}


export default App;
